// If I import anything from @obg/common.registration, the bundle size will increase...
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { CustomerSupportService } from "@obg/core.customer-support";
import { GtmLoginMethod } from "@obg/gtm.types/adaptive";
import { AlertButtonColor } from "@obg/mat.ui.core";
import {
	CommonState,
	Consents,
	CookieConsentConfigService,
	CookieConsentSelector,
	DataProtectionConfigService,
	DataProtectionEventName,
	MARKETING_SETTINGS_MESSAGE_CODE,
	MARKETING_SETTINGS_MESSAGE_CONTEXT,
	MarketingSettingsAction,
	MarketingSettingsSelector,
	MarketingSettingsWithTokenRequest,
} from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	AUTH_MESSAGE_CODES,
	AUTH_MESSAGE_CONTEXT,
	CORE_ROUTE,
	CoreState,
	COUNTRY_OF_REGISTRATION,
	CustomerManagementSelector,
	Jurisdiction,
	JurisdictionSelector,
	MarketingOffers,
	MessageService,
	MessageType,
	SessionSelector,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { DevicePostfixPipe, ignoreNil } from "@obg/ngx.global";
import { TelemetryService } from "@obg/ngx.global.evo";
import { RouteSelector } from "@obg/ngx.router";
import { head, isNil, some } from "lodash-es";
import { combineLatest, merge, Subscription } from "rxjs";
import { concatAll, filter, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { LoginFlowLoaderService } from "../new-login-flow/login-flow-loader.service";

import { RegistrationStepsTrackerService } from "./../../../common.registration/registration/registration-steps-tracker.service";
import {
	RegistrationStepDataConfirmedRegistration,
	REGISTRATION_STEP_PREFIX,
	SpidGtmRegistrationMethod,
	SpidGtmEventActions,
	SpidGtmRegistrationType,
} from "./../../../common.registration/registration/spid-register/spid-gtm.model";
import { SPIDRegistrationStatusService } from "./../../../common.registration/registration/spid-register/spid-storage.service";
import {
	AffiliateMetadata,
	RegistrationFeatureState,
	RegistrationSelector,
	RegistrationService,
} from "./../../../common.registration/sdk";
import { RegistrationWithSPIDState } from "./../../../common.registration/sdk/registration/spid/registration-with-spid.model";
import {
	COMMUNICATIONS_FIELD,
	LOGIN_CONSENTS_ERROR_ALERT_ID,
	LOGIN_CONSENTS_TIMEOUT_ERROR,
	LOGIN_CONSENTS_TRANSLATION_KEYS,
} from "./login-consents.const";

@Component({
	selector: "obg-m-login-consents-container",
	templateUrl: "./login-consents.container.html",
	styleUrls: ["./login-consents.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	providers: [DevicePostfixPipe, SPIDRegistrationStatusService],
})
export class LoginConsentsContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-login-consents") hostCssClass = true;

	@Input() titleText = "";
	@Input() authMethod?: GtmLoginMethod | undefined;

	@Output() loginError = new EventEmitter<void>();
	@Output() submitConsent = new EventEmitter<void>();

	missingMarketingConsents: Partial<Consents> | undefined;
	consentsToken: string | undefined;
	formGroup: UntypedFormGroup;
	subscriptionsFormGroup: UntypedFormGroup;
	initialSubscriptions: MarketingOffers | undefined;
	marketingConsentEnabled: boolean;
	isOneTrustEnabled: boolean;
	shouldAskCookieConsent = false;
	isLoading = false;
	isSubmitButtonDisabled = true;
	registrationType: Jurisdiction | GtmLoginMethod | undefined;
	hideGroupConsent = false;
	marketingConsentGiven: boolean | undefined;

	captionKey = "";
	acceptKey = LOGIN_CONSENTS_TRANSLATION_KEYS.generalYes;
	declineKey = LOGIN_CONSENTS_TRANSLATION_KEYS.generalNo;
	registrationWithSPIDState: RegistrationWithSPIDState | undefined;

	private subscriptions$$ = new Subscription();
	private cookieConsentConfigurationEnabled: boolean;
	private readonly _registrationBaseStep: number = 5 as const;

	constructor(
		private store: Store<CoreState & CommonState & RegistrationFeatureState & RegistrationWithSPIDState>,
		private formBuilder: UntypedFormBuilder,
		private dataProtectionConfigService: DataProtectionConfigService,
		private cookieConsentSelector: CookieConsentSelector,
		private marketingSettingsAction: MarketingSettingsAction,
		private sessionSelector: SessionSelector,
		private marketingSettingsSelector: MarketingSettingsSelector,
		private changeDetector: ChangeDetectorRef,
		private messageService: MessageService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private devicePostfix: DevicePostfixPipe,
		private telemetryService: TelemetryService,
		private alertService: AbstractTranslatableAlertService,
		private cookieConsentConfigService: CookieConsentConfigService,
		private loaderService: LoginFlowLoaderService,
		private customerSupportService: CustomerSupportService,
		private spidRegistrationStatusService: SPIDRegistrationStatusService,
		private _registrationSelector: RegistrationSelector,
		private _registrationStepsTrackerService: RegistrationStepsTrackerService,
		private _registrationService: RegistrationService
	) {
		this.cookieConsentConfigurationEnabled = !this.dataProtectionConfigService.getCookieConsentDisabled();
		this.marketingConsentEnabled = !this.dataProtectionConfigService.getMarketingConsentDisabled();
		this.isOneTrustEnabled = !!this.cookieConsentConfigService.getOneTrustIsEnabled();

		this.formGroup = this.formBuilder.group({
			cookie: undefined,
			communications: undefined,
		});

		this.subscriptionsFormGroup = this.formBuilder.group({
			acceptEmailOffers: undefined,
			acceptSmsOffers: undefined,
			acceptTelesalesOffers: undefined,
		});
	}

	ngOnInit() {
		const registrationState$ = this.store.pipe(
			select(this._registrationSelector.getSPIDRegistrationState()),
			ignoreNil(),
			tap((registrationState: RegistrationWithSPIDState) => (this.registrationWithSPIDState = registrationState))
		);

		// TODO: OBGB-10993. unify login errors handling between multiple places (registration, gdpr, login pages/modes etc)
		const loginError$ = this.messageService
			.consume$({
				context: [
					AUTH_MESSAGE_CONTEXT.login,
					AUTH_MESSAGE_CONTEXT.loginWithToken,
					AUTH_MESSAGE_CONTEXT.loginWithPersonalNumber,
				],
				messageType: MessageType.Error,
				messageCode: AUTH_MESSAGE_CODES.login.customerNotAllowed,
			})
			.pipe(
				concatAll(),
				withLatestFrom(this.store.select(RouteSelector.getCurrent)),
				filter(([, route]) => route?.name !== CORE_ROUTE.registration.register),
				tap(() => {
					this.isLoading = false;
					const message = `login.${AUTH_MESSAGE_CODES.login.customerNotAllowed}`.toLowerCase();

					this.alertService.open(
						{
							id: LOGIN_CONSENTS_ERROR_ALERT_ID,
							title: LOGIN_CONSENTS_TRANSLATION_KEYS.loginConsentsTitleError,
							message,
							hideClose: true,
							dialogConfig: { disableClose: true },
							buttons: [
								{
									text: LOGIN_CONSENTS_TRANSLATION_KEYS.generalClose,
									handler: () => this.loginError.emit(),
								},
								{
									color: AlertButtonColor.Primary,
									text: LOGIN_CONSENTS_TRANSLATION_KEYS.generalContactCustomerSupport,
									handler: () => this.customerSupportService.openSupportPageInNewTab(),
								},
							],
						},
						[]
					);
				})
			);
		const cookieConsentMessage$ = this.messageService
			.consume$({
				context: MARKETING_SETTINGS_MESSAGE_CONTEXT.consents,
				messageCode: MARKETING_SETTINGS_MESSAGE_CODE.updateMarketingSettingsWithTokenFail,
				messageType: MessageType.Error,
			})
			.pipe(
				tap(() => (this.isLoading = false)),
				switchMap(() =>
					this.snackBarService.openSnackBar$(
						{
							message: LOGIN_CONSENTS_TRANSLATION_KEYS.generalGenericError,
							action: LOGIN_CONSENTS_TRANSLATION_KEYS.generalClose,
							duration: 4000,
							type: MessageType.Error,
						},
						[TranslationGroup.Common]
					)
				)
			);

		const hasAcceptedCookies$ = this.store.pipe(
			select(this.cookieConsentSelector.isAccepted()),
			take(1),
			tap((cookieAccepted) => (this.shouldAskCookieConsent = !this.isOneTrustEnabled && !cookieAccepted))
		);

		const consentsToken$ = this.store.pipe(
			select(this.sessionSelector.getConsentsNotAcceptedToken()),
			tap((token) => (this.consentsToken = token))
		);

		const hideGroupConsent$ = this.store
			.select(CustomerManagementSelector.getPersistentData<string>(COUNTRY_OF_REGISTRATION))
			.pipe(
				tap(
					(countryOfRegistration) =>
						(this.hideGroupConsent = this.dataProtectionConfigService
							.getHideGroupConsentCountries()
							.includes(countryOfRegistration!))
				)
			);

		const missingConsents$ = this.store.pipe(
			select(this.marketingSettingsSelector.getMissingMarketingSettings()),
			ignoreNil(),
			take(1),
			tap((missingMarketingSettings) => {
				this.registrationType = this.authMethod || missingMarketingSettings.jurisdiction;
				this.missingMarketingConsents = missingMarketingSettings.consents;
				this.uiInteractionConsentOpen();
				if (!this.missingMarketingConsents) {
					this.captionKey = "";
					return;
				}
				if (this.missingMarketingConsents!.brandMarketing && this.missingMarketingConsents!.groupMarketing) {
					this.captionKey = this.devicePostfix.transform(
						LOGIN_CONSENTS_TRANSLATION_KEYS.generalConsentsCommunicationCaptionGroup
					);
					return;
				}
				if (this.missingMarketingConsents.groupMarketing) {
					this.captionKey = this.devicePostfix.transform(
						LOGIN_CONSENTS_TRANSLATION_KEYS.loginConsentsCommunicationsCaptionOnlyGroup
					);
					return;
				}

				this.captionKey = LOGIN_CONSENTS_TRANSLATION_KEYS.loginConsentsCommunicationsCaptionBrand;

				if (this.dataProtectionConfigService.getEnableOnlyBrandMarketingConsentUpdate()) {
					this.acceptKey = LOGIN_CONSENTS_TRANSLATION_KEYS.loginConsentsCommunicationsAcceptBrand;
					this.declineKey = LOGIN_CONSENTS_TRANSLATION_KEYS.loginConsentsCommunicationsDeclineBrand;
				}
			})
		);

		const initialDetails$ = this.store.pipe(
			select(this.marketingSettingsSelector.getMissingMarketingSettingsDetails()),
			filter((details) => !!details),
			tap((details) => {
				if (details!.consents) {
					this.formGroup.patchValue(details!.consents);
				}
				if (details!.subscriptions) {
					this.subscriptionsFormGroup.patchValue(details!.subscriptions!);
				}
			})
		);

		const currentRoute$ = combineLatest([
			this.store.select(RouteSelector.getCurrent),
			this.store.select(JurisdictionSelector.get),
		]).pipe(
			filter(([route, jurisdiction]) => !!route && !!jurisdiction),
			take(1),
			tap(([route, jurisdiction]) => {
				if (route!.name === CORE_ROUTE.registration.register) {
					this.titleText =
						jurisdiction === Jurisdiction.Ksa
							? LOGIN_CONSENTS_TRANSLATION_KEYS.loginConsentsCaptionDutchRegister
							: LOGIN_CONSENTS_TRANSLATION_KEYS.loginConsentsCaptionRegister;
					return;
				}

				if (
					this.missingMarketingConsents?.brandMarketing &&
					!this.missingMarketingConsents?.groupMarketing &&
					this.dataProtectionConfigService.getEnableOnlyBrandMarketingConsentUpdate()
				) {
					this.titleText = LOGIN_CONSENTS_TRANSLATION_KEYS.loginConsentsCaptionLoginBrand;
					return;
				}

				this.titleText = LOGIN_CONSENTS_TRANSLATION_KEYS.loginConsentsCaptionLogin;
			})
		);

		const formValueChanges$ = merge(this.formGroup.valueChanges, this.subscriptionsFormGroup.valueChanges).pipe(
			filter(() => !!this.missingMarketingConsents),
			tap(() => {
				const isCookieInvalid =
					this.shouldAskCookieConsent &&
					this.missingMarketingConsents!.cookie &&
					isNil(this.formGroup.controls.cookie.value);

				const isCommunicationsInvalid =
					!this.hideGroupConsent &&
					this.marketingConsentEnabled &&
					(this.missingMarketingConsents!.brandMarketing || this.missingMarketingConsents!.groupMarketing) &&
					isNil(this.formGroup.controls.communications.value);

				const isSubscriptionsInvalid =
					this.missingMarketingConsents!.brandMarketing &&
					this.formGroup.controls.communications.value &&
					!!this.subscriptionsFormGroup &&
					!some(this.subscriptionsFormGroup.controls, (control: AbstractControl) => control.value);

				this.isSubmitButtonDisabled = isCookieInvalid || isCommunicationsInvalid || isSubscriptionsInvalid;
			})
		);

		const observables$ = [
			registrationState$,
			consentsToken$,
			hideGroupConsent$,
			missingConsents$,
			initialDetails$,
			formValueChanges$,
			currentRoute$,
			cookieConsentMessage$,
			loginError$,
		];

		if (this.cookieConsentConfigurationEnabled) {
			observables$.push(hasAcceptedCookies$);
		}

		for (const observable$ of observables$) {
			this.subscriptions$$.add(observable$.subscribe(() => this.changeDetector.markForCheck()));
		}
	}

	ngOnDestroy() {
		this.subscriptions$$?.unsubscribe();
	}

	onSubmit() {
		if (!this.consentsToken || !this.missingMarketingConsents || !this.formGroup) {
			return;
		}
		this.isLoading = true;

		const nullToUndefined = (value: boolean | null) => (value === null ? undefined : value);
		const { communications, cookie } = this.formGroup.controls;
		const isConsentAccepted = nullToUndefined(communications.value) ?? this.marketingConsentGiven;
		const groupMarketing = this.hideGroupConsent
			? false
			: this.missingMarketingConsents.groupMarketing
				? isConsentAccepted
				: undefined;
		const brandMarketing = this.missingMarketingConsents.brandMarketing ? isConsentAccepted : undefined;
		const cookieConsent =
			(!this.shouldAskCookieConsent && this.missingMarketingConsents.cookie) ||
			this.isOneTrustEnabled ||
			nullToUndefined(cookie.value);

		let subscriptions: MarketingOffers | undefined;

		if (this.missingMarketingConsents.brandMarketing && communications.value && this.subscriptionsFormGroup) {
			subscriptions = this.subscriptionsFormGroup.value;
		}

		if (
			this.missingMarketingConsents.brandMarketing &&
			this.marketingConsentGiven !== undefined &&
			this.dataProtectionConfigService.getEnableOnlyBrandMarketingConsentUpdate()
		) {
			subscriptions = {
				acceptEmailOffers: isConsentAccepted,
				acceptSmsOffers: isConsentAccepted,
				acceptTelesalesOffers: isConsentAccepted,
			};
		}

		const request: MarketingSettingsWithTokenRequest = {
			token: this.consentsToken,
			consents: {
				thirdParty: true,
				cookie: cookieConsent,
				groupMarketing,
				brandMarketing,
			},
			subscriptions,
		};

		this.telemetryService.track({
			event: DataProtectionEventName.ConsentSubmitted,
			RegistrationType: this.registrationType,
		});

		this.store.dispatch(this.marketingSettingsAction.updateMarketingSettingsWithToken(request));
		this.loaderService.toggleLoading(true);
		this.submitConsent.emit();

		const error$ = this.store.pipe(
			select(this.marketingSettingsSelector.getError()),
			filter(
				(error) =>
					!!error &&
					error?.code === LOGIN_CONSENTS_TIMEOUT_ERROR.code &&
					error?.type === LOGIN_CONSENTS_TIMEOUT_ERROR.type
			),
			tap(() => {
				this.loginError.emit();
			})
		);

		this.subscriptions$$.add(error$.subscribe());
	}

	setMarketingConsent(value: boolean) {
		this.marketingConsentGiven = value;
		this.uiInteractionFieldEvent(COMMUNICATIONS_FIELD);
	}

	uiInteractionFieldEvent(field: string) {
		this.telemetryService.track({
			event: DataProtectionEventName.ConsentChange,
			RegistrationType: this.registrationType,
			ToggleField: field,
		});
	}

	private uiInteractionConsentOpen() {
		if (this.spidRegistrationStatusService.getUserRegisteredWithSPID()) {
			const affiliateData: AffiliateMetadata = head(this._registrationService.getAffiliateMetaData())!;

			this.telemetryService.track({
				PartnerID: affiliateData?.affiliate || "Missing",
				GUID: this.registrationWithSPIDState?.registrationToken?.token || "Missing",
				HashedEmail: this.registrationWithSPIDState?.registrationToken?.hashedEmail || "Missing",
				RegistrationType: SpidGtmRegistrationType.SPID,
				RegistrationMethod: SpidGtmRegistrationMethod.SPID,
				RegistrationStep: `${REGISTRATION_STEP_PREFIX} ${this._registrationBaseStep}`,
				event: SpidGtmEventActions.CONFIRMED_REGISTRATION,
			} satisfies RegistrationStepDataConfirmedRegistration);
			this._registrationStepsTrackerService.eraseLastSuccessRegistrationStepDataFromStorage();

			this.spidRegistrationStatusService.setUserRegisteredWithSPID(false);
		} else {
			this.telemetryService.track({
				event: DataProtectionEventName.ConsentOpen,
				RegistrationType: this.registrationType,
			});
		}
	}
}
